import { useQuery } from '@tanstack/react-query';
import { nuiCtx } from 'nike-ui/src/common/initialization';
import { cakeCmsClient } from 'nike-ui/src/common/axios/cakeCmsClient';
import ScratchCardApiResponse, {
    ListScratchCardSectionSettings,
    PageScratchCardSectionSettings,
    OverallScratchCardSectionSettings,
    ProcessedData
} from './types';
import axios from 'axios';
import { NikeCasinoApi } from '../../types/generated/nike-casino-api';
import { isProfileIScratchCardEnabled } from '../../profiles';

/**
 * @param inputData JSON data from API with some predefined structure
 */
const processApiData = (inputData: ScratchCardApiResponse): ProcessedData => {
    const data = inputData.scratchcard_section_settings;
    const overview = data[0] as OverallScratchCardSectionSettings;

    const navigation = [];
    let scratchCardSection = null;
    const scratchCardCategories = [];
    const otherSections = [];
    const scratchCards = [];

    // we are skipping section as this is not valid tab
    for (let i = 1; i < data.length; i += 1) {
        const page = data[i] as PageScratchCardSectionSettings;

        navigation.push({
            label: page.header_name,
            path: page.slug,
            section: page.section
        });

        if (page.hasOwnProperty('scratchcards_list')) {
            const scratchCardList = page as ListScratchCardSectionSettings;
            scratchCardSection = {
                header: scratchCardList.header_name,
                description: scratchCardList.description
            };

            // scratchcards have categories we don't care here
            for (const categoryKey of Object.keys(scratchCardList.scratchcards_list)) {
                const category = {
                    name: categoryKey,
                    scratchcards: []
                };

                for (const scratchCard of scratchCardList.scratchcards_list[categoryKey]) {
                    const { name, price, max_win, photo, slug, flag_new, thumbnail_photo, description } = scratchCard;

                    const enhancedScratchCard = {
                        name,
                        price,
                        description,
                        slug,
                        maxWin: max_win,
                        imageURL: photo ? `${inputData.scratchcard_path}${photo}` : null,
                        thumbnailURL: `${inputData.scratchcard_path}${thumbnail_photo}`,
                        isNew: flag_new
                    };
                    category.scratchcards.push(enhancedScratchCard);
                    scratchCards.push(enhancedScratchCard);
                }

                scratchCardCategories.push(category);
            }
        } else {
            otherSections.push(data[i]);
        }
    }

    return {
        overview,
        keys: Object.keys(data),
        navigation,
        sections: {
            scratchCardSection: {
                ...scratchCardSection,
                categories: scratchCardCategories
            },
            otherSections
        },
        scratchCards: scratchCards
    };
};

export const createThumbnailUrl = slug => {
    return `${nuiCtx.properties['cake-cms.image.link.url']}scratchcard_section/${slug}`;
};

export const useLotteryData = () =>
    useQuery(
        ['lottery-data'],
        async () => {
            const result = await cakeCmsClient.get('/generic/scratchcard-section-settings');
            return processApiData((result as unknown) as ScratchCardApiResponse);
        },
        { staleTime: Infinity, cacheTime: Infinity }
    );

const processIScratchCardData = gamebookResp => {
    return Object.values(gamebookResp?.structure?.tabs?.SCRATCH?.categories ?? []) as NikeCasinoApi.CategoryDto[];
};

export const useIScratchCardData = (filteredCategory?: string) => {
    return useQuery(
        ['i-scratch-card-data', filteredCategory],
        async () => {
            if (!isProfileIScratchCardEnabled()) {
                return [];
            }
            const result = await axios.post<never, NikeCasinoApi.FilteredGamebookResp>('/api/casino/gamebook-filter', {
                attributes: {
                    'tab.id': {
                        type: 'S=',
                        values: ['SCRATCH']
                    },
                    ...maxStake(filteredCategory)
                }
            });
            return processIScratchCardData(result);
        },
        { staleTime: Infinity, cacheTime: Infinity }
    );
};

const maxStake = (filteredCategory: string) => {
    if (filteredCategory) {
        return {
            'game.maxStake': {
                type: 'BD=',
                values: [Number(filteredCategory)]
            }
        };
    } else {
        return {};
    }
};
